// Pitch video URLs come from the PUBLIC, unauthenticated submission forms, and
// are later rendered as links (plus an inline player for our own uploads) on
// the reviewer-facing pitch pages. An unvalidated value there is a stored-
// injection vector: a javascript:/data: URI in a link, or an attacker-
// controlled origin framed inside the trusted portal (phishing / clickjacking
// against admins). This module is the single gate — validate on submit AND on
// render (defense in depth, since docs already in the database were never
// validated).
//
// Applicants host their videos wherever they like (YouTube, Vimeo, Loom,
// Google Drive, Dropbox, ...), so a link may point at any http(s) host. No
// third-party page is ever framed: the only inline player is a <video> for
// files the pitch forms uploaded to our own Storage bucket.

import { app } from "../firebaseConfig";

// Storage folder the pitch forms upload videos into (see storage.rules).
export const PITCH_VIDEO_FOLDER = "pitch-videos";

// Returns the normalized http(s) URL if it is safe to store and link, else
// null. A link pasted without a scheme ("youtu.be/abc") is read as https.
export function safeVideoUrl(raw) {
  if (!raw || typeof raw !== "string") return null;
  let text = raw.trim();
  // A real link never contains whitespace; two pasted links would merge.
  if (!text || /\s/.test(text)) return null;
  if (!/^[a-z][a-z0-9+.-]*:/i.test(text)) text = `https://${text}`;
  let url;
  try {
    url = new URL(text);
  } catch {
    return null;
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") return null;
  // A video link names a real host and a specific page on it. That rejects
  // placeholders ("N/A", "none.") and bare file names ("IMG_1234.MOV"),
  // which would otherwise parse as hostnames.
  const labels = url.hostname.split(".");
  const tld = labels[labels.length - 1];
  if (labels.length < 2 || labels.includes("") || !/^([a-z]{2,}|xn--[a-z0-9-]+)$/.test(tld)) {
    return null;
  }
  if (url.pathname === "/" && !url.search) return null;
  return url.href;
}

export function isSafeVideoUrl(raw) {
  return safeVideoUrl(raw) !== null;
}

// Returns the URL if it is a pitch video uploaded to our own Storage bucket
// (safe to play in a <video> element), else null.
export function uploadedVideoSrc(raw) {
  const href = safeVideoUrl(raw);
  if (!href) return null;
  const url = new URL(href);
  if (url.protocol !== "https:" || url.hostname !== "firebasestorage.googleapis.com") {
    return null;
  }
  let path;
  try {
    path = decodeURIComponent(url.pathname);
  } catch {
    return null;
  }
  const prefix = `/v0/b/${app.options.storageBucket}/o/${PITCH_VIDEO_FOLDER}/`;
  return path.startsWith(prefix) ? href : null;
}

// The third-party host a pitch video link leads to ("youtu.be"), shown next
// to the link so reviewers can see where it goes before opening it. Null for
// invalid links and for our own uploads.
export function externalVideoHost(raw) {
  const href = safeVideoUrl(raw);
  if (!href || uploadedVideoSrc(href)) return null;
  return new URL(href).hostname.replace(/^www\./, "");
}
